import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  ClockIcon, 
  CalendarIcon,
  TagIcon,
  CheckCircleIcon,
  PlayCircleIcon,
  PauseCircleIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  ListBulletIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import 'react-calendar/dist/Calendar.css';
import '../styles/Calendar.css';
import { Goal, SubGoal } from './Goals';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

interface LearningLogEntry {
  id: string;
  title: string;
  date: string;
  type: string;
  duration: number;
  description: string;
  tags: string[];
  resourceUrl?: string;
  reflection: string;
  rating: number;
  mood: string;
  categoryId: string;
}

interface ScheduledSession {
  id: string;
  title: string;
  date: string;
  time: string;
  duration: number;
  description: string;
  categoryId: string;
}

interface Category {
  id: string;
  name: string;
  color: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  type: 'goal' | 'subgoal';
  status: 'not_started' | 'in_progress' | 'completed';
  parentGoal?: string;
}

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

export default function CalendarPage() {
  const { currentUser } = useAuth();
  const [value, setValue] = useState<Value>(new Date());
  const [entries, setEntries] = useState<LearningLogEntry[]>([]);
  const [scheduledSessions, setScheduledSessions] = useState<ScheduledSession[]>(() => {
    const saved = localStorage.getItem('scheduledSessions');
    return saved ? JSON.parse(saved) : [];
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [showNewSession, setShowNewSession] = useState(false);
  const [editingSession, setEditingSession] = useState<ScheduledSession | null>(null);
  const [newSession, setNewSession] = useState<Partial<ScheduledSession>>({
    title: '',
    date: new Date().toISOString().split('T')[0],
    time: '09:00',
    duration: 60,
    description: '',
    categoryId: ''
  });
  const [goals, setGoals] = useState<Goal[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    if (!currentUser) return;
    const fetchData = async () => {
      const entriesRef = collection(db, 'learningLogEntries');
      const categoriesRef = collection(db, 'learningLogCategories');
      const entriesQ = query(entriesRef, where('uid', '==', currentUser.uid));
      const categoriesQ = query(categoriesRef, where('uid', '==', currentUser.uid));
      const [entriesSnap, categoriesSnap] = await Promise.all([
        getDocs(entriesQ),
        getDocs(categoriesQ)
      ]);
      const loadedEntries = entriesSnap.docs.map(doc => doc.data() as LearningLogEntry);
      const loadedCategories = categoriesSnap.docs.map(doc => doc.data() as Category);
      setEntries(loadedEntries);
      setCategories(loadedCategories);
      if (loadedCategories.length > 0) {
        setNewSession(prev => ({ ...prev, categoryId: loadedCategories[0].id }));
      }
    };
    fetchData();
  }, [currentUser]);

  useEffect(() => {
    localStorage.setItem('scheduledSessions', JSON.stringify(scheduledSessions));
  }, [scheduledSessions]);

  useEffect(() => {
    const savedGoals = localStorage.getItem('learningGoals');
    if (savedGoals) {
      const parsedGoals = JSON.parse(savedGoals);
      setGoals(parsedGoals);

      // Convert goals and subgoals to calendar events
      const calendarEvents: CalendarEvent[] = [];
      parsedGoals.forEach((goal: Goal) => {
        // Add goal deadline
        calendarEvents.push({
          id: goal.id,
          title: goal.title,
          date: new Date(goal.deadline),
          type: 'goal',
          status: goal.status
        });

        // Add subgoal deadlines
        goal.subgoals.forEach((subGoal: SubGoal) => {
          calendarEvents.push({
            id: subGoal.id,
            title: subGoal.title,
            date: new Date(subGoal.deadline),
            type: 'subgoal',
            status: subGoal.status,
            parentGoal: goal.title
          });
        });
      });

      setEvents(calendarEvents);
    }
  }, []);

  const handleDateChange = (newValue: Value) => {
    setValue(newValue);
    if (newValue instanceof Date) {
      setSelectedDate(newValue);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newSession.title || !newSession.date || !newSession.time || !newSession.categoryId) {
      alert('Please fill in all required fields');
      return;
    }

    const session: ScheduledSession = {
      id: editingSession?.id || Date.now().toString(),
      title: newSession.title,
      date: newSession.date,
      time: newSession.time,
      duration: newSession.duration || 60,
      description: newSession.description || '',
      categoryId: newSession.categoryId
    };

    if (editingSession) {
      setScheduledSessions(sessions => 
        sessions.map(s => s.id === session.id ? session : s)
      );
      setEditingSession(null);
    } else {
      setScheduledSessions(sessions => [...sessions, session]);
    }

    setNewSession({
      title: '',
      date: new Date().toISOString().split('T')[0],
      time: '09:00',
      duration: 60,
      description: '',
      categoryId: categories[0]?.id || ''
    });
    setShowNewSession(false);
  };

  const handleEdit = (session: ScheduledSession) => {
    setEditingSession(session);
    setNewSession(session);
    setShowNewSession(true);
  };

  const handleDelete = (sessionId: string) => {
    if (window.confirm('Are you sure you want to delete this scheduled session?')) {
      setScheduledSessions(sessions => sessions.filter(s => s.id !== sessionId));
    }
  };

  // Helper to get local date string in YYYY-MM-DD
  const getLocalDateString = (date: Date) => date.toLocaleDateString('en-CA');

  const getSessionsForDate = (date: Date) => {
    const dateStr = getLocalDateString(date);
    return scheduledSessions.filter(session => session.date === dateStr);
  };

  const getEntriesForDate = (date: Date) => {
    const dateStr = getLocalDateString(date);
    return entries.filter(entry => entry.date === dateStr);
  };

  // For calendar markers: get all logs for a date, grouped by category (unique)
  const getEntryCategoriesForDate = (date: Date) => {
    const dateStr = getLocalDateString(date);
    // Only unique category ids
    return Array.from(new Set(entries.filter(entry => entry.date === dateStr).map(entry => entry.categoryId)));
  };

  const getEventsForDate = (date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
    });
  };

  // Enhanced tile content with better visual indicators
  const tileContent = ({ date }: { date: Date }) => {
    const entryCategories = getEntryCategoriesForDate(date);
    const dayEvents = getEventsForDate(date);
    const daySessions = getSessionsForDate(date);

    if (entryCategories.length === 0 && dayEvents.length === 0 && daySessions.length === 0) return null;

    return (
      <div className="absolute top-1 left-0 right-0 z-10">
        <div className="flex flex-col items-center space-y-1">
          {/* Learning logs */}
          {entryCategories.length > 0 && (
            <div className="flex space-x-1">
              {entryCategories.slice(0, 3).map((catId, idx) => (
                <span
                  key={catId + idx}
                  className={`w-2 h-2 rounded-full ${getCategoryColor(catId)} shadow-sm`}
                  title={categories.find(c => c.id === catId)?.name || ''}
                />
              ))}
              {entryCategories.length > 3 && (
                <span className="text-xs text-gray-500">+{entryCategories.length - 3}</span>
              )}
            </div>
          )}
          
          {/* Goals and subgoals */}
          {dayEvents.length > 0 && (
            <div className="flex space-x-1">
              {dayEvents.slice(0, 2).map(event => (
                <span
                  key={event.id}
                  className={`w-2 h-2 rounded-full ${
                    event.status === 'completed'
                      ? 'bg-green-500'
                      : event.status === 'in_progress'
                      ? 'bg-blue-500'
                      : 'bg-gray-500'
                  } shadow-sm`}
                  title={`${event.title} (${event.type === 'subgoal' ? `Subgoal of ${event.parentGoal}` : 'Goal'})`}
                />
              ))}
              {dayEvents.length > 2 && (
                <span className="text-xs text-gray-500">+{dayEvents.length - 2}</span>
              )}
            </div>
          )}

          {/* Scheduled sessions */}
          {daySessions.length > 0 && (
            <div className="flex space-x-1">
              {daySessions.slice(0, 2).map(session => (
                <span
                  key={session.id}
                  className="w-2 h-2 rounded-full bg-purple-500 shadow-sm"
                  title={`${session.title} at ${session.time}`}
                />
              ))}
              {daySessions.length > 2 && (
                <span className="text-xs text-gray-500">+{daySessions.length - 2}</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.color || 'bg-gray-500';
  };

  const getEventsForSelectedDate = () => {
    return getEventsForDate(selectedDate);
  };

  // Future events: sessions with date > today
  const todayStr = getLocalDateString(new Date());
  const futureSessions = scheduledSessions
    .filter(session => session.date > todayStr)
    .sort((a, b) => a.date.localeCompare(b.date));

  // Filter sessions by category
  const filteredFutureSessions = selectedCategory === 'all' 
    ? futureSessions 
    : futureSessions.filter(session => session.categoryId === selectedCategory);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <PlayCircleIcon className="w-5 h-5 text-blue-500" />;
      default:
        return <XCircleIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!currentUser) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Learning Calendar
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mt-2">
                Plan, track, and visualize your learning journey
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setViewMode('calendar')}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === 'calendar' 
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' 
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                title="Calendar View"
              >
                <Squares2X2Icon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === 'list' 
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' 
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                title="List View"
              >
                <ListBulletIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowNewSession(true)}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Schedule Session
              </button>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-6">
          <div className="flex items-center space-x-3">
            <FunnelIcon className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by:</span>
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All Categories
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <span className={`inline-block w-2 h-2 rounded-full ${category.color} mr-2`}></span>
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {viewMode === 'calendar' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Calendar Section */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6">
                <Calendar
                  onChange={handleDateChange}
                  value={value}
                  tileContent={tileContent}
                  className="w-full border-none modern-calendar"
                  tileClassName={({ date }) => {
                    const today = new Date();
                    const isToday = date.getDate() === today.getDate() &&
                                   date.getMonth() === today.getMonth() &&
                                   date.getFullYear() === today.getFullYear();
                    
                    if (isToday) {
                      return 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white font-bold rounded-lg';
                    }
                    
                    const hasEvents = getSessionsForDate(date).length > 0 || 
                                    getEntriesForDate(date).length > 0 || 
                                    getEventsForDate(date).length > 0;
                    
                    if (hasEvents) {
                      return 'bg-blue-50 dark:bg-blue-900/20 rounded-lg';
                    }
                    
                    return '';
                  }}
                />
              </div>
            </div>

            {/* Right Sidebar - Selected Date Details */}
            <div className="space-y-6">
              {value instanceof Date && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <CalendarIcon className="w-6 h-6 text-blue-500" />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {value.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </h2>
                  </div>

                  {/* Legend */}
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Calendar Legend</h3>
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center space-x-2">
                        <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                        <span className="text-gray-600 dark:text-gray-400">Today</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                        <span className="text-gray-600 dark:text-gray-400">Scheduled Session</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        <span className="text-gray-600 dark:text-gray-400">Completed Goal</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                        <span className="text-gray-600 dark:text-gray-400">In Progress</span>
                      </div>
                    </div>
                  </div>

                  {/* Activities for Selected Date */}
                  {getSessionsForDate(value).length === 0 && getEntriesForDate(value).length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                        <CalendarIcon className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 mb-4">No activities scheduled for this day</p>
                      <button
                        onClick={() => setShowNewSession(true)}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Schedule Something
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Scheduled Sessions */}
                      {getSessionsForDate(value).map(session => (
                        <div key={session.id} className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900 dark:text-white">{session.title}</h3>
                              <div className="flex items-center space-x-2 mt-1 text-sm text-gray-600 dark:text-gray-300">
                                <ClockIcon className="w-4 h-4" />
                                <span>{session.time} • {session.duration} minutes</span>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEdit(session)}
                                className="p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(session.id)}
                                className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {session.description && (
                            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{session.description}</p>
                          )}
                          <div className="mt-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(session.categoryId)} text-white`}>
                              <TagIcon className="w-3 h-3 mr-1" />
                              {categories.find(c => c.id === session.categoryId)?.name}
                            </span>
                          </div>
                        </div>
                      ))}

                      {/* Learning Logs */}
                      {getEntriesForDate(value).map(entry => (
                        <div key={entry.id} className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-green-200 dark:border-green-700">
                          <h3 className="font-semibold text-gray-900 dark:text-white">{entry.title}</h3>
                          <div className="flex items-center space-x-2 mt-1 text-sm text-gray-600 dark:text-gray-300">
                            <ClockIcon className="w-4 h-4" />
                            <span>{entry.duration} minutes • {entry.type}</span>
                          </div>
                          <div className="mt-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(entry.categoryId)} text-white`}>
                              <TagIcon className="w-3 h-3 mr-1" />
                              {categories.find(c => c.id === entry.categoryId)?.name}
                            </span>
                          </div>
                        </div>
                      ))}

                      {/* Goals and Subgoals */}
                      {getEventsForDate(value).map(event => (
                        <div key={event.id} className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-700">
                          <div className="flex items-start space-x-3">
                            {getStatusIcon(event.status)}
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900 dark:text-white">{event.title}</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                {event.type === 'subgoal' ? `Subgoal of ${event.parentGoal}` : 'Goal'}
                              </p>
                            </div>
                          </div>
                          <div className="mt-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(event.status)}`}>
                              {event.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* List View */
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">All Scheduled Sessions</h2>
            {filteredFutureSessions.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <CalendarIcon className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">No future sessions scheduled</p>
                <button
                  onClick={() => setShowNewSession(true)}
                  className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
                >
                  Schedule Your First Session
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFutureSessions.map(session => (
                  <div key={session.id} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{session.title}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-300">
                          <div className="flex items-center space-x-1">
                            <CalendarIcon className="w-4 h-4" />
                            <span>{session.date}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <ClockIcon className="w-4 h-4" />
                            <span>{session.time} • {session.duration} min</span>
                          </div>
                        </div>
                        {session.description && (
                          <p className="mt-3 text-gray-600 dark:text-gray-300">{session.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(session.categoryId)} text-white`}>
                          <TagIcon className="w-3 h-3 mr-1" />
                          {categories.find(c => c.id === session.categoryId)?.name}
                        </span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(session)}
                            className="p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(session.id)}
                            className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* New Session Modal */}
        {showNewSession && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                {editingSession ? 'Edit Session' : 'Schedule New Session'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={newSession.title}
                    onChange={(e) => setNewSession({ ...newSession, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Date
                    </label>
                    <input
                      type="date"
                      value={newSession.date}
                      onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Time
                    </label>
                    <input
                      type="time"
                      value={newSession.time}
                      onChange={(e) => setNewSession({ ...newSession, time: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={newSession.duration}
                    onChange={(e) => setNewSession({ ...newSession, duration: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    min="15"
                    step="15"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={newSession.categoryId}
                    onChange={(e) => setNewSession({ ...newSession, categoryId: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    required
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description (optional)
                  </label>
                  <textarea
                    value={newSession.description}
                    onChange={(e) => setNewSession({ ...newSession, description: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    rows={3}
                  />
                </div>

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewSession(false);
                      setEditingSession(null);
                      setNewSession({
                        title: '',
                        date: new Date().toISOString().split('T')[0],
                        time: '09:00',
                        duration: 60,
                        description: '',
                        categoryId: categories[0]?.id || ''
                      });
                    }}
                    className="px-6 py-3 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    {editingSession ? 'Save Changes' : 'Schedule Session'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 